/**
 * Organization servicing log (business pages slice 3, migrations 2026-10-06-320/321/322).
 *
 * - Picklists: Subject / Task Type, Activity Type, Related Goal / Session live in three
 *   admin-editable tables. Nothing here hard-codes their values.
 * - Payload builders are pure so the exact shape sent to public.interactions is unit-tested
 *   (interactions.servicing.test.ts). The DB suite
 *   database/tests/test_business_pages_slice3_servicing.py inserts the same keys.
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  Interaction,
  InteractionLookupList,
  InteractionLookupValue,
  InteractionMethod,
  InteractionPurpose,
} from "../types/interaction.types";
import { REWARD_INTERACTION_TYPES } from "./interactions.service";

export const LOOKUP_TABLES: Record<InteractionLookupList, string> = {
  subjects: "interaction_subjects",
  activity_types: "interaction_activity_types",
  goal_sessions: "interaction_goal_sessions",
};

export const LOOKUP_LABELS: Record<InteractionLookupList, string> = {
  subjects: "Subject / Task Type",
  activity_types: "Activity Type",
  goal_sessions: "Related Goal / Session",
};

const LOOKUP_COLUMNS =
  "id, label, sort_order, is_active, is_placeholder, created_at, updated_at";

/** All values of one list (active and retired), in display order. */
export const getLookupValues = async (
  list: InteractionLookupList,
): Promise<InteractionLookupValue[]> => {
  const { data, error } = await supabase
    .from(LOOKUP_TABLES[list])
    .select(LOOKUP_COLUMNS)
    .order("sort_order", { ascending: true })
    .order("label", { ascending: true })
    .limit(500);
  if (error) {
    throw error;
  }
  return (data || []) as InteractionLookupValue[];
};

/** Admin only (RLS: active admin/superadmin). */
export const createLookupValue = async (
  list: InteractionLookupList,
  label: string,
  sortOrder: number,
): Promise<InteractionLookupValue> => {
  const { data, error } = await supabase
    .from(LOOKUP_TABLES[list])
    .insert({ label: label.trim(), sort_order: sortOrder })
    .select(LOOKUP_COLUMNS);
  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error("The value was not added.");
  }
  return data[0] as InteractionLookupValue;
};

/** Admin only. Values are never deleted: retire them with is_active = false. */
export const updateLookupValue = async (
  list: InteractionLookupList,
  id: UUID,
  patch: Partial<
    Pick<
      InteractionLookupValue,
      "label" | "sort_order" | "is_active" | "is_placeholder"
    >
  >,
): Promise<InteractionLookupValue> => {
  const body = { ...patch };
  if (typeof body.label === "string") {
    body.label = body.label.trim();
  }
  const { data, error } = await supabase
    .from(LOOKUP_TABLES[list])
    .update(body)
    .eq("id", id)
    .select(LOOKUP_COLUMNS);
  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error(
      "The value was not changed: only an active admin can edit these lists.",
    );
  }
  return data[0] as InteractionLookupValue;
};

/** Event-reward activity (migration 220): organization follows the event; staff-only to edit. */
export const isRewardInteraction = (
  i: Pick<Interaction, "type"> | null | undefined,
): boolean =>
  !!i?.type &&
  (REWARD_INTERACTION_TYPES as readonly string[]).includes(i.type as string);

/** Support-task activity may have no organization (filed under an organization-less customer). */
export const isSupportTaskInteraction = (
  i: Pick<Interaction, "support_task_id"> | null | undefined,
): boolean => !!i?.support_task_id;

export interface ServicingFormValues {
  title: string;
  subject_id: string;
  activity_type_id: string;
  goal_session_id: string;
  interaction_date: string;
  interaction_time: string;
  next_follow_up_date: string;
  call_plan: string;
  results: string;
  description: string; // legacy "Notes"; only shown when editing a row that has one
  purpose: InteractionPurpose;
  method: InteractionMethod;
  organization_id: string;
  location_id: string;
  duration_minutes: string;
}

const blankToNull = (v: string | undefined | null): string | null => {
  if (v === undefined || v === null) {
    return null;
  }
  const t = v.trim();
  return t === "" ? null : t;
};

export interface ServicingValidationContext {
  isEditing: boolean;
  original?: Interaction | null;
  /** organization fixed by the page (organization detail) */
  fixedOrganizationId?: string;
}

/** Field errors. Subject + Activity Date are required on create (PDF p.4); legacy rows that never
 * had a subject are not blocked on unrelated edits, except when a Call Plan/Results is added
 * (the database then requires a Subject: interactions_internal_needs_subject_check). */
export const validateServicingForm = (
  v: ServicingFormValues,
  ctx: ServicingValidationContext,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const addsNotes = !!blankToNull(v.call_plan) || !!blankToNull(v.results);
  const needsSubject =
    !ctx.isEditing ||
    !!ctx.original?.subject_id ||
    !!ctx.original?.is_internal ||
    addsNotes;
  if (needsSubject && !v.subject_id) {
    errors.subject_id = "Subject is required";
  }
  if (!v.interaction_date) {
    errors.interaction_date = "Activity Date is required";
  }
  if (
    v.next_follow_up_date &&
    v.interaction_date &&
    v.next_follow_up_date < v.interaction_date
  ) {
    errors.next_follow_up_date =
      "Next Task Due cannot be before the Activity Date";
  }
  // Organization: required for ordinary activity. Reward rows follow their event; support-task rows
  // follow the customer (may have none).
  const orgOptional =
    isRewardInteraction(ctx.original) || isSupportTaskInteraction(ctx.original);
  if (!orgOptional && !ctx.fixedOrganizationId && !v.organization_id) {
    errors.organization_id = "Organization is required";
  }
  if (v.duration_minutes && !/^\d{1,4}$/.test(v.duration_minutes.trim())) {
    errors.duration_minutes = "Duration must be a whole number of minutes";
  }
  return errors;
};

/** Title is NOT NULL in the database but not part of the PDF form: default it to the Subject. */
const resolveTitle = (v: ServicingFormValues, subjectLabel?: string): string =>
  blankToNull(v.title) || subjectLabel || "Servicing activity";

/** INSERT payload for a new servicing activity (always an internal record). */
export const buildServicingCreatePayload = (
  v: ServicingFormValues,
  ctx: { userId: UUID; organizationId?: string; subjectLabel?: string },
): Record<string, unknown> => ({
  organization_id: ctx.organizationId || v.organization_id,
  location_id: blankToNull(v.location_id),
  title: resolveTitle(v, ctx.subjectLabel),
  purpose: v.purpose,
  method: v.method,
  interaction_date: v.interaction_date,
  interaction_time: blankToNull(v.interaction_time),
  duration_minutes: blankToNull(v.duration_minutes)
    ? parseInt(v.duration_minutes, 10)
    : null,
  subject_id: blankToNull(v.subject_id),
  activity_type_id: blankToNull(v.activity_type_id),
  goal_session_id: blankToNull(v.goal_session_id),
  call_plan: blankToNull(v.call_plan),
  results: blankToNull(v.results),
  next_follow_up_date: blankToNull(v.next_follow_up_date),
  is_internal: true,
  user_id: ctx.userId,
  created_by: ctx.userId,
});

/**
 * UPDATE payload: only the fields that changed. Never sends user_id/created_by (the reward trigger
 * preserves the issuer anyway), never sends organization_id for a reward row (it follows the event:
 * migration 220) and never sends is_internal (a record does not change visibility from the editor).
 * A row that gains a Call Plan or Results becomes internal (interactions_internal_notes_check).
 */
export const buildInteractionUpdatePayload = (
  original: Interaction,
  v: ServicingFormValues,
  ctx: { fixedOrganizationId?: string; subjectLabel?: string },
): Record<string, unknown> => {
  const next: Record<string, unknown> = {
    title: resolveTitle(
      { ...v, title: v.title || original.title || "" },
      ctx.subjectLabel,
    ),
    purpose: v.purpose,
    method: v.method,
    interaction_date: v.interaction_date,
    interaction_time: blankToNull(v.interaction_time),
    duration_minutes: blankToNull(v.duration_minutes)
      ? parseInt(v.duration_minutes, 10)
      : null,
    subject_id: blankToNull(v.subject_id),
    activity_type_id: blankToNull(v.activity_type_id),
    goal_session_id: blankToNull(v.goal_session_id),
    call_plan: blankToNull(v.call_plan),
    results: blankToNull(v.results),
    next_follow_up_date: blankToNull(v.next_follow_up_date),
    description: blankToNull(v.description),
    location_id: blankToNull(v.location_id),
  };
  if (!isRewardInteraction(original) && !ctx.fixedOrganizationId) {
    next.organization_id = blankToNull(v.organization_id);
  }
  const norm = (x: unknown) =>
    x === undefined || x === "" ? null : (x as unknown);
  const origTime = original.interaction_time
    ? String(original.interaction_time).slice(0, 5)
    : null;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(next)) {
    let before = norm((original as unknown as Record<string, unknown>)[k]);
    let after = norm(val);
    if (k === "interaction_time") {
      before = origTime;
      after = after ? String(after).slice(0, 5) : null;
    }
    if (k === "interaction_date" && typeof before === "string") {
      before = before.split("T")[0];
    }
    if (before !== after) {
      out[k] = val;
    }
  }
  if ((out.call_plan || out.results) && !original.is_internal) {
    out.is_internal = true;
  }
  return out;
};
